import { parseEmailAddress } from "@qw/qw-common";

type ParsedParticipant = ReturnType<typeof parseEmailAddress>;

export interface ConversationLinkSender {
  primary: ParsedParticipant;
  participants: Array<ParsedParticipant & { isPrimary: boolean }>;
}

const UNKNOWN_SENDER: ParsedParticipant = { fullName: "Unknown Sender", firstName: "Unknown", email: "Unknown Sender" };

const normalizeEmail = (email?: string | null) => email?.trim().toLowerCase() ?? "";

const dedupeParticipants = (participants: ParsedParticipant[]) => {
  const seen = new Set<string>();

  return participants.filter((participant) => {
    const email = normalizeEmail(participant.email);

    if (seen.has(email)) {
      return false;
    }

    seen.add(email);
    return true;
  });
};

export const getConversationLinkSender = ({
  currentUserEmail,
  internalInboxEmails = [],
  lastMessageFrom,
  participants,
}: {
  currentUserEmail?: string | null;
  internalInboxEmails?: readonly (string | null | undefined)[];
  lastMessageFrom?: string | null;
  participants: string[];
}): ConversationLinkSender => {
  const parsedLastMessageFrom = lastMessageFrom?.trim() ? parseEmailAddress(lastMessageFrom) : null;
  const parsedParticipants = participants
    .map((participant) => participant.trim())
    .filter(Boolean)
    .map((participant) => parseEmailAddress(participant));
  const internalInboxEmailSet = new Set(
    internalInboxEmails.map((email) => normalizeEmail(email)).filter((email) => !!email)
  );
  const isInternalInboxEmail = (email: string) => internalInboxEmailSet.has(normalizeEmail(email));

  const primary =
    parsedLastMessageFrom ??
    parsedParticipants.find((participant) => !isInternalInboxEmail(participant.email)) ??
    parsedParticipants[0] ??
    UNKNOWN_SENDER;

  const normalizedPrimaryEmail = normalizeEmail(primary.email);
  const normalizedCurrentUserEmail = normalizeEmail(currentUserEmail);
  const orderedParticipants = dedupeParticipants([
    primary,
    ...parsedParticipants,
    ...(parsedLastMessageFrom ? [parsedLastMessageFrom] : []),
  ]);
  const displayParticipants = normalizedCurrentUserEmail
    ? orderedParticipants.filter((participant) => {
        const participantEmail = normalizeEmail(participant.email);

        return participantEmail === normalizedPrimaryEmail || participantEmail !== normalizedCurrentUserEmail;
      })
    : orderedParticipants;
  const visibleParticipants = displayParticipants.length > 0 ? displayParticipants : orderedParticipants;

  return {
    primary,
    participants: visibleParticipants.map((participant) => ({
      ...participant,
      isPrimary: normalizeEmail(participant.email) === normalizedPrimaryEmail,
    })),
  };
};
